import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { brokerAddCustomer } from '../api';
import { projLabel, stLabel } from './UnitsDeals/constants';

// Add-customer modal for brokers, surfaced from إدارة العملاء (the Leads page).
// Enforces the dedup rule server-side: a phone already in the CRM (developer
// sales OR any other broker) is rejected with a generic 409 — we never reveal
// who holds it. Owners may assign the new customer to one of their employees.
export default function BrokerAddCustomerModal({ onClose, onAdded, isBrokerOwner = false, salesTeam = [], t, lang, isRTL }) {
  const ar = lang === 'ar';
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [assignee, setAssignee] = useState('');
  const [project, setProject] = useState('');
  const [unit, setUnit] = useState('');
  const [projects, setProjects] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    axios.get('/api/broker/projects').then((r) => setProjects(r.data.projects || [])).catch(() => setProjects([]));
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  useEffect(() => {
    setUnit('');
    setUnitOptions([]);
    if (!project) return;
    let cancelled = false;
    axios.get('/api/broker/units', { params: { project } })
      .then((r) => { if (!cancelled) setUnitOptions(r.data.units || []); })
      .catch(() => { if (!cancelled) setUnitOptions([]); });
    return () => { cancelled = true; };
  }, [project]);

  const submit = async () => {
    setMsg(null);
    const n = name.trim(), p = phone.trim();
    if (!n || !p) { setMsg({ type: 'error', text: ar ? 'الاسم ورقم الجوال مطلوبان' : 'Name and phone are required' }); return; }
    setBusy(true);
    const res = await brokerAddCustomer({
      name: n, phone: p, project, unit,
      employee_name: isBrokerOwner ? assignee.trim() : '',
      assigned_to: isBrokerOwner ? assignee.trim() : '',
    });
    setBusy(false);
    if (res.error === 'duplicate_phone') {
      // Generic on purpose — never reveal whether it's the developer or a rival broker.
      setMsg({ type: 'dup', text: ar ? 'هذا العميل مسجّل مسبقاً (لدى المطوّر أو وسيط آخر) ولا يمكن إضافته' : "This customer is already registered (with the developer or another broker) and can't be added" });
      return;
    }
    if (res.error === 'invalid_phone') { setMsg({ type: 'error', text: ar ? 'رقم الجوال غير صحيح' : 'Invalid phone number' }); return; }
    if (res.error) { setMsg({ type: 'error', text: ar ? 'تعذّر إضافة العميل' : 'Could not add customer' }); return; }
    if (onAdded) onAdded();
    onClose();
  };

  const msgColor = msg ? (msg.type === 'dup' ? '#f59e0b' : '#ef4444') : null;

  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div className="crm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }} dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="crm-modal-header">
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>{ar ? 'إضافة عميل' : 'Add customer'}</h2>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>&times;</button>
        </div>
        <div className="crm-form-grid">
            <div className="input-group">
              <label>{ar ? 'اسم العميل' : 'Customer name'}</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder={ar ? 'الاسم الكامل' : 'Full name'} />
            </div>
            <div className="input-group">
              <label>{ar ? 'رقم الجوال' : 'Phone'}</label>
              <input style={{ direction: 'ltr', textAlign: ar ? 'right' : 'left' }} value={phone} onChange={(e) => setPhone(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} placeholder="05XXXXXXXX" inputMode="tel" />
            </div>
            {isBrokerOwner && (
              <div className="input-group">
                <label>{ar ? 'إسناد إلى موظف' : 'Assign to employee'}</label>
                <select value={assignee} onChange={(e) => setAssignee(e.target.value)}>
                  <option value="">{ar ? '— أنا —' : '— Me —'}</option>
                  {salesTeam.map((s) => { const nm = s.name || s; return <option key={nm} value={nm}>{nm}</option>; })}
                </select>
              </div>
            )}
            <div className="input-group">
              <label>{ar ? 'المشروع' : 'Project'}</label>
              <select value={project} onChange={(e) => setProject(e.target.value)}>
                <option value="">{ar ? '— اختر —' : '— Select —'}</option>
                {projects.map((p) => <option key={p.project} value={p.project}>{projLabel(p.project, t)}</option>)}
              </select>
            </div>
            <div className="input-group">
              <label>{ar ? 'الوحدة' : 'Unit'}</label>
              <select value={unit} onChange={(e) => setUnit(e.target.value)} disabled={!project} style={{ opacity: project ? 1 : 0.6 }}>
                <option value="">{project ? (ar ? '— اختر —' : '— Select —') : (ar ? 'اختر مشروعاً أولاً' : 'Pick a project first')}</option>
                {unitOptions.map((u) => <option key={u.id} value={u.unit_code}>{u.unit_code}{u.status ? ` · ${stLabel(u.status, lang)}` : ''}</option>)}
              </select>
            </div>
          </div>
          {msg && <div style={{ marginTop: 10, fontSize: '0.84rem', fontWeight: 700, color: msgColor }}>{msg.text}</div>}
        <div className="crm-modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>{ar ? 'إلغاء' : 'Cancel'}</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>{busy ? (ar ? 'جارٍ الإضافة…' : 'Adding…') : (ar ? 'إضافة عميل' : 'Add customer')}</button>
        </div>
      </div>
    </div>
  );
}
